import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getStyleById } from "@/constants/artStyles";
import { useToast } from "@/hooks/use-toast";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";
import { Copy, Share2, ShieldCheck } from "lucide-react";
import { useMemo } from "react";

interface PrivacySafeShareCardProps {
  selectedStyle: string;
  variant?: "card" | "compact";
  onCopied?: () => void;
  onShared?: () => void;
}

const SHARE_UTM_PARAMS = {
  utm_source: "customer_share",
  utm_medium: "organic",
  utm_campaign: "post_generation_share",
};

const buildShareUrl = (selectedStyle: string) => {
  const style = getStyleById(selectedStyle);
  const pathname = style ? `/styles/${style.id}` : "/create";
  const url = new URL(pathname, window.location.origin);

  Object.entries(SHARE_UTM_PARAMS).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return {
    styleName: style?.name || selectedStyle,
    url: url.toString(),
  };
};

export const PrivacySafeShareCard = ({
  selectedStyle,
  variant = "card",
  onCopied,
  onShared,
}: PrivacySafeShareCardProps) => {
  const { toast } = useToast();
  const { styleName, url } = useMemo(
    () => buildShareUrl(selectedStyle),
    [selectedStyle]
  );

  const shareText = `J'ai transformé ma photo en tableau style ${styleName} avec Ma Photo en Tableau. Tu peux créer le tien ici : ${url}`;

  const copyShareText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      trackFunnelEvent("post_generation_share_copied", {
        style: selectedStyle,
        target: "generic_style_page",
      });
      toast({
        title: "Message copié",
        description: "Le lien de partage ne contient aucune image ni donnée privée.",
      });
      onCopied?.();
    } catch {
      toast({
        title: "Copie impossible",
        description: "Vous pouvez copier le lien depuis la barre d'adresse de la page style.",
        variant: "destructive",
      });
    }
  };

  const shareNative = async () => {
    if (!navigator.share) {
      await copyShareText();
      return;
    }

    try {
      await navigator.share({
        title: "Ma Photo en Tableau",
        text: `J'ai créé un tableau personnalisé style ${styleName}.`,
        url,
      });

      trackFunnelEvent("post_generation_share_opened", {
        style: selectedStyle,
        target: "generic_style_page",
      });
      onShared?.();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast({
        title: "Partage impossible",
        description: "Essayez plutôt de copier le message de partage.",
        variant: "destructive",
      });
    }
  };

  const content = (
    <>
      <div className="flex items-start gap-3 rounded-2xl bg-background/70 p-4 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
        <p>
          Partage sans image privée : le lien renvoie vers la page publique du style,
          jamais vers votre fichier, votre commande ou vos informations personnelles.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={shareNative} className="flex-1">
          <Share2 className="mr-2 h-4 w-4" />
          Partager le style
        </Button>
        <Button variant="outline" onClick={copyShareText} className="flex-1">
          <Copy className="mr-2 h-4 w-4" />
          Copier le message
        </Button>
      </div>
    </>
  );

  if (variant === "compact") {
    return <div className="space-y-3">{content}</div>;
  }

  return (
    <Card className="gradient-card rounded-[28px] border-primary/15 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Share2 className="h-5 w-5" />
          Faire découvrir ce style
        </CardTitle>
        <CardDescription>
          Transformez votre création en bouche-à-oreille sans exposer votre image.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{content}</CardContent>
    </Card>
  );
};
